<template>
  <div class="Vimeo"
    :style="vimeoStyle"
    @click="play"
    @mouseenter="hover = true"
    @mouseleave="hover = false">

    <div class="Vimeo-container">

      <transition name="Vimeo-preview-">

        <div class="Vimeo-preview"
          v-show="showPreview && image">

          <tag class="Vimeo-button"
            icon="play"
            :label="buttonLabel"
            :meta="duration"
            :highlighted="hover"
            large />

          <image-component class="Vimeo-image"
            v-if="image"
            v-bind="image" />

        </div>

      </transition>

      <transition name="Vimeo-video-">

        <div class="Vimeo-video"
          v-show="!showPreview"
          :id="videoId"
          v-html="data.html">
        </div>

      </transition>

    </div>

  </div>
</template>

<script>
import axios from 'axios'
import Player from '@vimeo/player'
import { buildQueryString, millisecondsToMinutesAndSeconds } from 'helpers'

const VIMEO_EP = 'https://vimeo.com/api/oembed.json?url=https://vimeo.com/'

export default {
  name: 'Vimeo',

  props: {
    /**
     * The Vimeo resource ID
     */
    id: {
      type: [Number, String],
      required: true
    },

    /**
     * Image to use for the poster
     */
    image: {
      type: Object
    },

    /**
     * Ratio expressed as [width, height]
     */
    ratio: {
      type: Array,
      default () {
        return [16, 9]
      }
    },

    /**
     * Label text for the button
     */
    buttonLabel: {
      type: String,
      default () {
        return 'Play film'
      }
    }
  },

  data () {
    return {
      /**
       * Unique id for the video element
       * @type {String}
       */
      videoId: '',

      /**
       * Video data returned from API
       * @type {Object}
       */
      data: {
        duration: 0
      },

      /**
       * Whether to show the preview
       * @type {Boolean}
       */
      showPreview: true,

      /**
       * Hover state
       * @type {Boolean}
       */
      hover: false
    }
  },

  computed: {
    /**
     * Styles for the main element
     * @return {Object}
     */
    vimeoStyle () {
      const padding = this.ratio[1] / this.ratio[0] * 100

      return {
        'padding-bottom': `${padding}%`
      }
    },

    /**
     * Duration of video
     * @return {String}
     */
    duration () {
      if (!this.data.duration) return ''
      return millisecondsToMinutesAndSeconds(this.data.duration)
    }
  },

  created () {
    this.showPreview = this.image
    this.load()
  },

  methods: {
    /**
     * Load video information from Vimeo
     */
    async load () {
      const queryString = buildQueryString({
        byline: false,
        portrait: false,
        title: false
      })

      try {
        const { data } = await axios.get(`${VIMEO_EP}${this.id}${queryString}`)
        this.data = data
        this.videoId = `video-${this._uid}`
        this.$nextTick(this.init)
      } catch (err) {
        console.error(err)
      }
    },

    /**
     * Initialize player
     */
    init () {
      this.player = new Player(this.videoId)
    },

    /**
     * Play the video
     */
    play () {
      this.showPreview = false
      this.player.play()
    }
  }
}
</script>

<style>
@import 'properties';

.Vimeo {
  position: relative;
  overflow: hidden;
}

.Vimeo:hover {
  cursor: pointer;
}

.Vimeo-container {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.Vimeo-preview {

}

.Vimeo-preview--enter-active,
.Vimeo-preview--leave-active {
  transition: opacity 0.5s ease;
}

.Vimeo-preview--enter,
.Vimeo-preview--leave-to {
  opacity: 0;
}

.Vimeo-button {
  position: absolute;
  z-index: 100;
  bottom: 12px;
  left: 12px;
}

.Vimeo-image {

}

.Vimeo-video--enter-active,
.Vimeo-video--leave-active {
  transition: opacity 1s ease;
}

.Vimeo-video--enter,
.Vimeo-video--leave-to {
  opacity: 0;
}

.Vimeo-video iframe {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// Vimeo.vue?cee24e2a